import { Badge, Card, Group, Paper, Progress, Skeleton, Stack, Text, Title } from '@mantine/core'
import { IconCalendar, IconReceipt, IconUsers } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import type { MRT_ColumnDef } from 'mantine-react-table'
import { MantineReactTable, useMantineReactTable } from 'mantine-react-table'
import { useMemo } from 'react'

import { StatInfoGraphic } from '@/components/StatInfoGraphic'
import { useGlobalState } from '@/hooks/useGlobalState'
import type { BillingPlan, Invoice } from '@/services/api/api.types'
import { formatBillingDate } from '@/utils/analytics'
import type { SeatPlan } from '@/utils/planUtils'
import { calculateSeatsUntilNextTier, getActiveSeatTiersSorted } from '@/utils/planUtils'

const seats = (count: number) => `${count} seat${count === 1 ? '' : 's'}`

const formatInvoiceDate = (date: string | null | undefined): string => {
  if (!date) return '—'
  try {
    const datePart = date.split('T')[0]
    const [year, month, day] = datePart.split('-').map(Number)
    const localDate = new Date(year, month - 1, day)
    if (isNaN(localDate.getTime())) return '—'
    return localDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return '—'
  }
}

// Takes the tiers already sorted, so the caller can also count them without a second pass.
function getNextTier(currentPlan: BillingPlan, sortedTiers: SeatPlan[]): SeatPlan | null {
  if (currentPlan.maxUsers === 0) {
    // Current plan is unlimited, no next tier
    return null
  }

  // Find the next tier (first plan with maxUsers > current plan's maxUsers)
  return sortedTiers.find(p => p.maxUsers === 0 || p.maxUsers > currentPlan.maxUsers) || null
}

export function BillingTab() {
  const { api } = useGlobalState()

  const { data: billingInfo, isLoading } = useQuery({
    queryKey: ['billing', 'info'],
    queryFn: () => api.getBillingInfo(),
  })

  const { data: plans } = useQuery({
    queryKey: ['plans'],
    queryFn: () => api.getPlans(),
    staleTime: 0,
    refetchOnMount: 'always',
  })

  const { data: invoices, isLoading: isLoadingInvoices } = useQuery({
    queryKey: ['billing', 'invoices'],
    queryFn: () => api.getInvoices(),
  })

  const columns = useMemo<MRT_ColumnDef<Invoice>[]>(
    () => [
      {
        accessorKey: 'billingDate',
        header: 'Billing Date',
        Cell: ({ cell }) => formatInvoiceDate(cell.getValue<string>()),
      },
      {
        id: 'period',
        header: 'Period',
        Cell: ({ row }) => {
          const start = formatInvoiceDate(row.original.periodStart)
          const end = formatInvoiceDate(row.original.periodEnd)
          return `${start} — ${end}`
        },
      },
      {
        accessorKey: 'userCount',
        header: 'Users',
      },
      {
        accessorKey: 'totalDollars',
        header: 'Amount',
        Cell: ({ cell }) => `$${cell.getValue<number>().toFixed(2)}`,
      },
      {
        accessorKey: 'paid',
        header: 'Status',
        Cell: ({ cell }) => (
          <Badge color={cell.getValue<boolean>() ? 'green' : 'orange'} variant="light">
            {cell.getValue<boolean>() ? 'Paid' : 'Unpaid'}
          </Badge>
        ),
      },
    ],
    [],
  )

  const table = useMantineReactTable({
    columns,
    data: invoices ?? [],
    enableColumnActions: false,
    enableColumnFilters: false,
    enablePagination: false,
    enableSorting: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    layoutMode: 'grid',
    mantinePaperProps: { shadow: 'none', withBorder: false },
    mantineTableProps: { striped: true },
    state: { isLoading: isLoadingInvoices },
  })

  const info = billingInfo?.billingInfo
  const activeSeatTiers = getActiveSeatTiersSorted(plans ?? [])
  const nextTier = info ? getNextTier(info.plan, activeSeatTiers) : null
  const isUnlimitedTier = info?.plan.maxUsers === 0
  // An unlimited plan is the top tier on its own terms. Otherwise a missing next tier only
  // means "highest tier" once there are tiers to have compared against, which rules out
  // both a failed fetch and a list holding no active seat tiers.
  const isHighestTier = !!info && (isUnlimitedTier || (activeSeatTiers.length > 0 && !nextTier))
  const progressPercent = isUnlimitedTier ? 0 : ((info?.currentSubscribers ?? 0) / (info?.plan.maxUsers ?? 1)) * 100
  const subscribersDisplay = isUnlimitedTier
    ? `${info?.currentSubscribers ?? 0} (Unlimited)`
    : `${info?.currentSubscribers ?? 0} / ${info?.plan.maxUsers ?? 0}`
  const seatsUntilTierLimit = isUnlimitedTier
    ? 0
    : Math.max(0, (info?.plan.maxUsers ?? 0) - (info?.currentSubscribers ?? 0))
  // Tier bands include their own maxUsers, so the next tier starts one seat past the limit.
  const seatsUntilNextTier = calculateSeatsUntilNextTier(info?.currentSubscribers ?? 0, info?.plan.maxUsers ?? 0) ?? 0
  // Which of the two is true depends on knowing the tier list. Without it neither "next
  // tier" nor "tier limit" is a claim worth making, so the line says nothing at all.
  const seatsLabel = nextTier
    ? `${seats(seatsUntilNextTier)} until next tier`
    : isHighestTier
      ? `${seats(seatsUntilTierLimit)} until tier limit`
      : null

  return (
    <Card shadow="sm" p="xl" radius="md" withBorder>
      <Stack gap="xl" px="20px">
        <div>
          <Title order={4} mb="md">
            Billing Information
          </Title>

          {isLoading ? (
            <Stack gap="md">
              <Skeleton height={80} />
              <Skeleton height={120} />
            </Stack>
          ) : info ? (
            <Stack gap="xl">
              <Group justify="space-between">
                <StatInfoGraphic
                  icon={IconCalendar}
                  label="Next Billing Date"
                  value={formatBillingDate(info.nextBillingDate, true) || '—'}
                  tooltipText="Date when your next billing cycle begins"
                />
                <StatInfoGraphic
                  icon={IconReceipt}
                  label="Estimated Bill"
                  value={`$${info.estimatedBill}`}
                  tooltipText="Estimated amount for the current billing period"
                />
                <StatInfoGraphic
                  icon={IconUsers}
                  label="Users"
                  value={subscribersDisplay}
                  tooltipText="Current users out of maximum for this tier"
                />
              </Group>

              <Paper p="lg" radius="md" withBorder>
                <Stack gap="md">
                  <Group justify="space-between" align="flex-start">
                    <div>
                      <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>
                        Current Plan
                      </Text>
                      <Text size="lg" fw={600}>
                        {info.plan.name}
                      </Text>
                      <Text size="sm" c="dimmed">
                        ${info.plan.priceDollars}/user/month
                      </Text>
                    </div>

                    {nextTier ? (
                      <div style={{ textAlign: 'right' }}>
                        <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>
                          Next Tier
                        </Text>
                        <Text size="lg" fw={600}>
                          {nextTier.name}
                        </Text>
                        <Text size="sm" c="dimmed">
                          ${nextTier.priceDollars}/user/month
                        </Text>
                      </div>
                    ) : isHighestTier ? (
                      <div style={{ textAlign: 'right' }}>
                        <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>
                          Tier Status
                        </Text>
                        <Text size="lg" fw={600} c="green">
                          Highest Tier
                        </Text>
                      </div>
                    ) : null}
                  </Group>

                  {!isUnlimitedTier && (
                    <div style={{ paddingTop: 8 }}>
                      <Progress value={progressPercent} size="xl" radius="xl" color="blue" />
                      <Group justify="space-between" mt={4}>
                        <Text size="xs" c="dimmed">
                          Tier Progress
                        </Text>
                        {seatsLabel && (
                          <Text size="xs" c="dimmed">
                            {seatsLabel}
                          </Text>
                        )}
                      </Group>
                    </div>
                  )}
                </Stack>
              </Paper>
            </Stack>
          ) : (
            <Text size="sm" c="dimmed">
              No billing information available.
            </Text>
          )}
        </div>

        <div style={{ paddingTop: 12, width: '100%' }}>
          <Title order={4} mb="md">
            Billing History
          </Title>
          <MantineReactTable table={table} />
        </div>
      </Stack>
    </Card>
  )
}
